import { IAddress } from '@commenda/types';
import { fetcher } from '@commenda/utils';

interface CustomerDetails {
  customerId?: string;
}

export interface CalculateSalesTaxPayload {
  corporationId: number;
  shipTo: IAddress;
  shipFrom?: IAddress | null;
  lineItems?: ILineItemInput[];
  customerDetails?: CustomerDetails;
  transactionDate?: string;
  transactionCurrency?: string;
  transactionType?: 'SALE' | 'REFUND' | 'RETURN';
  /** Calculates as if registered in the ship-to jurisdiction, to preview rates. */
  forceCalculation?: boolean;
}

export interface CalculateSalesTaxResult<TData = unknown> {
  success: boolean;
  data?: TData;
  error?: string;
  errorBody?: unknown;
}

export interface ILineItemInput {
  lineNumber: number;
  pricePerUnit: number;
  quantity: number;
  taxCode?: string;
  discount?: number;
  productId?: string;
}

export async function calculateSalesTax(
  payload: CalculateSalesTaxPayload,
  token?: string
): Promise<CalculateSalesTaxResult> {
  try {
    const body = {
      shipTo: payload.shipTo,
      shipFrom: payload.shipFrom ? payload.shipFrom : null,
      corporationId: payload.corporationId,
      ...(payload.lineItems ? { lineItems: payload.lineItems } : {}),
      ...(payload.customerDetails
        ? { customerDetails: payload.customerDetails }
        : {}),
      ...(payload.transactionDate
        ? { transactionDate: payload.transactionDate }
        : {}),
      ...(payload.transactionCurrency
        ? { transactionCurrency: payload.transactionCurrency }
        : {}),
      ...(payload.transactionType
        ? { transactionType: payload.transactionType }
        : {}),
      ...(payload.forceCalculation ? { forceCalculation: true } : {}),
    };

    const response = await fetcher(
      'new-sales-tax/internal/calculations/calculate',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      },
      token
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data?.message || 'Failed to calculate sales tax',
        errorBody: data,
      };
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}
